import axios from 'axios'
import ImageCrud from './ImageCrud.class'
import Agreement from './Event/Agreement'
import User, {UserWithStatusEvent} from './User.class'
import Module from './Module.class'
import Simple from './SimpleName/Simple.class'
import EventPlace from './Event/EventPlace.class'
import moment from 'moment'
import {get, isObject, cloneDeep} from 'lodash'
import Mentor from './Mentor.class'
import Form from './Form'
import Personal from './Event/Personal'
import Image from './ImageManager/Image'
import Author from './Event/Author'
import {REMOVE_IMAGE} from './constans'
import Preview from './Event/Preview'
import Company from './Company.class.js'
import Seo from './Seo.class'
import City from './City.class'
import Review from './Review.class'
import Timetable from './Event/Timetable'
import UserStatus from './User/UserStatus'
import { CERTIFICATE_PARAM_BASIC } from './Event/constants'

const baseURL = '/api/event/'

/**
 * @param {number | Object} init
 * @property {Simple} purpose
 * @property {string} name
 * @property {string} full_name
 * @property {string} info
 * @property {string} full_info
 * @property {boolean} published
 * @property {boolean} hidden
 * @property {number} participants_number
 * @property {User} responsible_person
 * @property {User} contact_user
 * @property {string} contact_email
 * @property {string} contact_phone
 * @property {string} close_text
 * @property {boolean} not_moderate
 * @property {string} site_url
 * @property {string} video_url
 * @property {string} materials_url
 * @property {Simple} reject_reason
 * @property {string} media_contact_person
 * @property {string} media_contact_email
 * @property {string} media_contact_phone
 * @property {Date} start_at
 * @property {Date} finish_at
 * @property {Date} reg_finish_at
 * @property {number} leader_id
 * @property {Simple} direction
 * @property {Array} themes
 * @property {Array} competencies
 * @property {Array} tags
 * @property {EventPlace} place
 * @property {Array<Company>} partner
 * @property {Array<Company>} organization
 * @property {Event} parent
 * @property {Event[]} modules
 * @property {ImageCrud} logo
 * @property {Image} new_logo
 * @property {Simple} type
 * @property {Simple} access
 * @property {string} place_comment
 * @property {string} certificate_name
 * @property {string} front_url
 * @property {number} number_hours
 * @property {string} custom_status_for_users
 * @property {Simple[]} game_modes
 * @property {string} feedback_form_url
 */
export default class Event {
	/**
	 * @constructor
	 * @param {number | Object} init
	 */
	constructor (init = {}) {
		this.id = null
		this.setDefaultData()

		if (typeof init === 'number') {
			this.id = init
			this.getSingle().then()
		} else {
			this.setDefaultData(init)
		}
	}

	setDefaultData (init = {}) {
		if (!init) return

		// for request
		// required field
		this.published = get(init, 'published') ? init.published : false
		this.published_at = get(init, 'published_at') ? new Date(init.published_at) : null
		this.hidden = get(init, 'hidden') ? init.hidden : false
		this.id = get(init, 'id') ? init.id : null
		this.publishTitle = get(init, 'publish_title', true)
		this.publishInfo = get(init, 'publish_info', true)
		this.name = get(init, 'name') ? init.name : ''
		this.full_name = get(init, 'full_name') ? init.full_name : ''
		this.info = get(init, 'info') ? init.info : ''
		this.full_info = get(init, 'full_info') ? init.full_info : ''
		this.contact_email = get(init, 'contact_email') ? init.contact_email : ''
		this.contact_phone = get(init, 'contact_phone') ? init.contact_phone : ''
		this.start_at = get(init, 'start_at') && this.isValidDate(init.start_at) ? new Date(init.start_at) : null
		this.finish_at = get(init, 'finish_at') && this.isValidDate(init.finish_at) ? new Date(init.finish_at) : null
		this.certificate_name = get(init, 'certificate_name') ? init.certificate_name : ''
		this.type = get(init, 'type.id') ? new Simple(init.type) : new Simple()
		this.serial_number = get(init, 'serial_number') ? init.serial_number : null

		// nullable filed
		this.rich_content = get(init, 'rich_content') && init.rich_content ? this.filterRichContent(this.createObjectFromJsonString(init.rich_content)) : []
		this.modules = get(init, 'modules') && Array.isArray(init.modules) ? this.setModules(init.modules) : []
		this.module_structure = get(init, 'module_structure') ? this.createModuleStructure(init.module_structure) : []
		this.authors = get(init, 'authors') && Array.isArray(init.authors) ? init.authors.map(el => new Author(el)) : []
		this.personal = get(init, 'personal') ? new Personal(init.personal) : new Personal()
		this.promo = get(init, 'promo') && Array.isArray(init.promo) ? this.createImageLogoArray(init.promo) : []
		this.photo_report = get(init, 'photo_report') && Array.isArray(init.photo_report) ? this.createImageLogoArray(init.photo_report) : []
		this.logo = get(init, 'logo') ? new ImageCrud(init.logo) : new ImageCrud()
		this.logo_micro = get(init, 'logo_micro') ? new ImageCrud(init.logo_micro) : new ImageCrud()
		this.agreement = get(init, 'agreement') ? new Agreement(init.agreement) : new Agreement()
		this.imageForBadgeTop = get(init, 'imageForBadgeTop') ? new ImageCrud(init.imageForBadgeTop) : new ImageCrud()
		this.imageForBadgeBottom = get(init, 'imageForBadgeBottom') ? new ImageCrud(init.imageForBadgeBottom) : new ImageCrud()
		this.new_logo = null
		this.new_logo_url = null
		this.new_logo_micro = null
		this.new_logo_micro_url = null
		this.new_agreement = null
		this.new_agreement_url = null
		this.new_imageForBadgeTop = null
		this.new_imageForBadgeTop_url = null
		this.new_imageForBadgeBottom = null
		this.new_imageForBadgeBottom_url = null
		this.reg_finish_at = get(init, 'reg_finish_at') && this.isValidDate(init.reg_finish_at) ? new Date(init.reg_finish_at) : null
		this.place = get(init, 'place.id') ? new EventPlace(init.place) : new EventPlace()
		this.city = get(init, 'city') ? new City(init.city) : null
		this.number_hours = get(init, 'number_hours') ? init.number_hours : 0
		this.number_modules = get(init, 'number_modules') ? init.number_modules : 0
		this.custom_status_for_users = get(init, 'custom_status_for_users') ? init.custom_status_for_users : ''
		this.certificate_content = get(init, 'certificate_content') ? init.certificate_content : ''
		this.show_certificate_content = get(init, 'show_certificate_content') ? init.show_certificate_content : false
		this.game_modes = get(init, 'game_modes') && Array.isArray(init.game_modes)
			? init.game_modes.map(el => new Simple(el))
			: []

		// for leader id
		this.responsible_person = get(init, 'responsible_person.id') ? new User(init.responsible_person) : new User()
		this.contact_user = get(init, 'contact_user.id') ? new User(init.contact_user) : new User()
		this.participants_number = get(init, 'participants_number') ? init.participants_number : null
		this.site_url = get(init, 'site_url') ? init.site_url : ''
		this.video_url = get(init, 'video_url') ? init.video_url : ''
		this.materials_url = get(init, 'materials_url') ? init.materials_url : ''
		this.media_contact_person = get(init, 'media_contact_person') ? init.media_contact_person : ''
		this.media_contact_email = get(init, 'media_contact_email') ? init.media_contact_email : ''
		this.media_contact_phone = get(init, 'media_contact_phone') ? init.media_contact_phone : ''
		this.reject_reason = get(init, 'reject_reason.id') ? new Simple(init.reject_reason) : new Simple()
		this.direction = get(init, 'direction.id') ? new Simple(init.direction) : new Simple()
		this.purpose = get(init, 'purpose.id') ? new Simple(init.purpose) : new Simple()
		this.access = get(init, 'access.id') ? new Simple(init.access) : new Simple()
		/** @deprecated */
		this.place_comment = get(init, 'place_comment') ? init.place_comment : ''
		this.close_text = get(init, 'close_text') ? init.close_text : ''
		this.leader_id = get(init, 'leader_id') ? init.leader_id : null
		this.eventFormat = get(init, 'event_format') ? this.setEventFormat(init.event_format) : {id: 0, name: 'Формат не определен'}
		this.eventLevel = get(init, 'event_level') ? this.setEventLevel(init.event_level) : {id: 0, name: 'Уровень не определен'}
		this.eventTemplate = get(init, 'event_template') ? this.setEventTemplate(init.event_template) : {id: 0, name: 'Шаблон не установлен'}

		// not request

		this.front_url = get(init, 'front_url') ? `${init.front_url}/events/${this.id}` : false
		this.front_url_original = get(init, 'front_url') ? init.front_url : false
		this.back_url = get(init, 'back_url') ? init.back_url : false
		this.certificatesForPrinting = get(init, 'certificates_for_printing') ? init.certificates_for_printing : false
		this.badgesForPrinting = get(init, 'badges_for_printing') && this.back_url ? this.back_url + init.badges_for_printing : ''
		this.not_moderate = get(init, 'not_moderate') ? Boolean(init.not_moderate) : false
		this.created_at = get(init, 'created_at') && this.isValidDate(init.created_at) ? new Date(init.created_at) : null
		this.updated_at = get(init, 'updated_at') && this.isValidDate(init.updated_at) ? new Date(init.updated_at) : null
		this.deleted_at = get(init, 'deleted_at') && this.isValidDate(init.deleted_at) ? new Date(init.deleted_at) : null
		this.themes = get(init, 'themes') && Array.isArray(init.themes.data) ? init.themes.data : []
		this.competencies = get(init, 'competencies') && Array.isArray(init.competencies.data) ? init.competencies.data : []
		this.tags = get(init, 'tags') && Array.isArray(init.tags.data) ? init.tags.data : []
		/** @type Company[] */
		this.partner = get(init, 'partner') && Array.isArray(init.partner) ? init.partner.map(args => new Company(args)) : []
		/** @type Company[] */
		this.organization = get(init, 'organization') && Array.isArray(init.organization) ? init.organization.map(args => new Company(args)) : []

		this.certificate_mentors = get(init, 'certificate_mentors') && Array.isArray(init.certificate_mentors) ? this.setMentors(init.certificate_mentors) : []
		this.errors = {}
		this.form = new Form(init.form, 'event', this.id)
		this.availableCount = 0
		this.timetable = get(init, 'timetable') && isObject(init.timetable)
			? Array.isArray(init.timetable) ? init.timetable.map(el => new Timetable(el))
				: Object.values(init.timetable).map(el => new Timetable(el)) : []
		this.applications_all = get(init, 'applications_all') ? init.applications_all : 0
		this.applications_approved = get(init, 'applications_approved') ? init.applications_approved : 0
		this.applications_pending = get(init, 'applications_pending') ? init.applications_pending : 0
		this.applications_rejected = get(init, 'applications_rejected') ? init.applications_rejected : 0
		this.graduated_applications = get(init, 'graduated_applications') ? init.graduated_applications : 0
		this.check = false
		this.calendar_links = get(init, 'calendar_links') ? init.calendar_links : {}
		this.preview = get(init, 'preview') ? new Preview(init.preview) : null
		this.reviews = get(init, 'reviews') ? this.setArrayClassObject(init.reviews, Review) : []

		this.seo = get(init, 'seo') ? new Seo(init.seo) : new Seo({
			model_type: 'App\\Event',
			model_id: this.id
		})
		this.own_status = get(init, 'own_status') ? new Simple(init.own_status) : null
		this.feedback_form_url = get(init, 'feedback_form_url') ? init.feedback_form_url : ''
		this.price = get(init, 'price', 0)

		this.institute = get(init, 'institute', {
			description: '',
			key: '',
			value: 0
		})

		this.head_show_partners = get(init, 'head_show_partners', false)
		this.register_condition = get(init, 'register_condition', [])

		if (Array.isArray(this.register_condition)) {
			this.register_condition = this.register_condition.map(v => Event.registerConditionList.find(vv => v === vv.key)).filter(v => !!v)
		} else {
			this.register_condition = []
		}
	}

	static url = baseURL

	static instituteList = [
		{
			value: 1,
			description: 'Академия наставников',
			key: 'ACADEMY'
		},
		{
			value: 2,
			description: 'Институт развития образования АСОУ',
			key: 'AOSD'
		}
	]

	static registerConditionList = [
		{
			description: 'Заполнено обучение в профиле',
			key: 'profile_has_education'
		}
	]
	/**
	 * get all events
	 * @param {Object} params
	 * @returns {Promise<{data: Event[]}>}
	 */
	static getAll (params) {
		return axios.get(baseURL, {
			params
		})
			.then(res => {
				return {
					...res.data,
					data: this.createObjectFromArray(res.data.data)
				}
			})
			.catch(err => {
				console.error(err)
				return null
			})
	}
	static getSinglePromise (id) {
		return axios.get(`/api/event/admin/${id}`)
			.then(res => {
				return new Event(res.data.data)
			})
			.catch(err => {
				console.log(err)
				return null
			})
	}
	static createObjectFromArray (arr) {
		return arr.map(item => new Event(item))
	}
	static importById (id) {
		const formData = new FormData()
		formData.append('leader_id[]', id)

		return axios.post(`${baseURL}synch`, formData)
			.then(res => {
				return res
			})
			.catch(err => {
				console.log(err)
				return null
			})
	}
	/**
	 * get event by id
	 * @returns {Promise<void>}
	 */
	async getSingle () {
		try {
			const {data} = await axios.get(`/api/event/admin/${this.id}`)
			this.setDefaultData(data.data)
		} catch (e) {
			console.error(e)
		}
	}
	get nameWithoutTags () {
		const regex = /<\/?[^>]+>/gi
		return this.name.replace(regex, '')
	}
	/**
	 * get form data
	 * @returns {FormData}
	 */
	get formData () {
		const formData = new FormData()
		this.module_structure.forEach((section) => {
			section.modules.forEach((module) => {
				delete (module.parent)
			})
		})

		// required field
		formData.append('hidden', this.hidden ? 1 : 0)
		formData.append('name', this.name)
		formData.append('full_name', this.full_name)
		formData.append('info', this.info)
		formData.append('full_info', this.full_info)
		if (this.certificate_name) {
			formData.append('certificate_name', this.certificate_name)
		}
		formData.append('publish_title', this.publishTitle ? 1 : 0)
		formData.append('publish_info', this.publishInfo ? 1 : 0)
		formData.append('event_format', this.eventFormat.id)
		if (this.isSpecialCourse) {
			formData.append('event_template', this.eventTemplate.id)
		}
		if (this.serial_number) {
			formData.append('serial_number', this.serial_number)
		}

		if (this.price && (this.isOnlineEvent || this.isOfflineEvent)) {
			formData.append('price', this.price)
		}

		// nullable field
		formData.append('rich_content', this.richContentForSave())
		formData.append('module_structure', this.moduleStructureForSave())
		formData.append('authors', this.authorsForSave())
		formData.append('reg_finish_at', this.reg_finish_at ? this.dateToServerFormat(this.reg_finish_at) : '')
		formData.append('start_at', this.start_at ? this.dateToServerFormat(this.start_at) : '')
		formData.append('finish_at', this.finish_at ? this.dateToServerFormat(this.finish_at) : '')
		formData.append('contact_phone', this.contact_phone)
		formData.append('contact_email', this.contact_email)
		formData.append('personal', this.personal.forSave)
		formData.append('feedback_form_url', this.feedback_form_url)
		formData.append('video_url', this.video_url)
		formData.append('institute', this.institute.value)

		if (this.isOnlineEvent) {
			formData.append('number_hours', this.number_hours)
			formData.append('number_modules', this.number_modules)
			formData.append('custom_status_for_users', this.custom_status_for_users)
		}
		if (get(this.eventLevel, 'id')) {
			formData.append('event_level', this.eventLevel.id)
		}
		if (get(this.type, 'id')) {
			formData.append('type_id', this.type.id)
		}
		if (this.new_logo) {
			if (this.new_logo === REMOVE_IMAGE) {
				formData.append('image', '')
			} else {
				formData.append('image', this.new_logo)
			}
		}
		if (this.new_logo_micro) {
			if (this.new_logo_micro === REMOVE_IMAGE) {
				formData.append('logo_micro', '')
			} else {
				formData.append('logo_micro', this.new_logo_micro)
			}
		}
		if (this.new_imageForBadgeTop) {
			if (this.new_imageForBadgeTop === REMOVE_IMAGE) {
				formData.append('imageForBadgeTop', '')
			} else {
				formData.append('imageForBadgeTop', this.new_imageForBadgeTop)
			}
		}
		if (this.new_imageForBadgeBottom) {
			if (this.new_imageForBadgeBottom === REMOVE_IMAGE) {
				formData.append('imageForBadgeBottom', '')
			} else {
				formData.append('imageForBadgeBottom', this.new_imageForBadgeBottom)
			}
		}
		if (this.new_agreement) {
			formData.append('agreement', this.new_agreement)
		}
		if (get(this.place, 'id')) {
			formData.append('place_id', this.place.id)
		}
		if (this.isOfflineEvent) {
			if (this.show_certificate_content) {
				formData.append('certificate_content', this.certificate_content)
			}
			formData.append('show_certificate_content', this.show_certificate_content ? 1 : 0)
		}

		// for leader id
		if (get(this.responsible_person, 'id')) {
			formData.append('responsible_person_id', this.responsible_person.id)
		}
		if (get(this.contact_user, 'id')) {
			formData.append('contact_user_id', this.contact_user.id)
		}
		if (this.participants_number) {
			formData.append('participants_number', this.participants_number)
		}
		if (this.site_url) {
			formData.append('site_url', this.site_url)
		}
		if (this.materials_url) {
			formData.append('materials_url', this.materials_url)
		}
		if (this.media_contact_person) {
			formData.append('media_contact_person', this.media_contact_person)
		}
		if (this.media_contact_email) {
			formData.append('media_contact_email', this.media_contact_email)
		}
		if (this.media_contact_phone) {
			formData.append('media_contact_phone', this.media_contact_phone)
		}
		if (get(this.reject_reason, 'id')) {
			formData.append('reject_reason_id', this.reject_reason.id)
		}
		if (get(this.direction, 'id')) {
			formData.append('direction_id', this.direction.id)
		}
		if (get(this.purpose, 'id')) {
			formData.append('purpose_id', this.purpose.id)
		}
		if (get(this.access, 'id')) {
			formData.append('access_id', this.access.id)
		}
		if (this.place_comment) {
			formData.append('place_comment', this.place_comment)
		}
		if (this.close_text) {
			formData.append('close_text', this.close_text)
		}
		if (this.leader_id) {
			formData.append('leader_id', this.leader_id)
		}
		if (get(this.city, 'id')) {
			formData.append('city_id', this.city.id)
		}
		if (
			this.game_modes &&
			Array.isArray(this.game_modes) &&
			this.game_modes.length > 0
		) {
			this.game_modes.forEach(a => {
				if (typeof a.id !== 'number') return
				formData.append('game_modes[]', String(a.id))
			})
		} else {
			formData.append('game_modes', '')
		}
		if (
			this.register_condition &&
			Array.isArray(this.register_condition) &&
			this.register_condition.length > 0
		) {
			this.register_condition.forEach(a => {
				if (a && a.key) {
					formData.append('register_condition[]', a.key)
				}
			})
		} else {
			formData.append('register_condition[]', null)
		}

		formData.append('head_show_partners', Number(this.head_show_partners))

		return formData
	}
	/**
	 * update event by id
	 * @returns {Promise}
	 */
	update () {
		return axios.post(`${baseURL}${this.id}`, this.formData)
			.then(res => {
				return res
			})
			.catch(err => {
				console.error(err)
				return err.response
			})
	}
	/**
	 * create event by id
	 * @returns {Promise}
	 */
	async create () {
		return await axios.post(`${baseURL}`, this.formData)
			.then(res => {
				return res
			})
			.catch(err => {
				console.error(err)
				return err.response
			})
	}
	/**
	 * delete event by id
	 * @returns {boolean}
	 */
	async delete () {
		try {
			const res = await axios.delete(`${baseURL}${this.id}`)
			return res
		} catch (e) {
			console.error(e)
			return false
		}
	}
	static deleteById (id) {
		return axios.delete(`${baseURL}${id}`)
			.then(res => {
				return res
			})
			.catch(err => {
				console.error(err)
				return false
			})
	}

	restore () {
		return axios.post(`${baseURL}${this.id}/restore`)
			.then(res => {
				return res
			})
			.catch(err => {
				console.error(err)
				return false
			})
	}

	publish () {
		const formData = new FormData()
		formData.append('date', this.published ? 0 : this.dateToServerFormat(new Date()))

		return axios.post(`${baseURL}${this.id}/publish`, formData)
			.then(res => {
				return res
			})
			.catch(err => {
				console.error(err)
				return err.response
			})
	}
	hide (status) {
		const formData = new FormData()
		formData.append('hidden', status ? 1 : 0)

		return axios.post(`${baseURL}${this.id}`, formData)
			.then(res => {
				return res
			})
			.catch(err => {
				console.error(err)
				return false
			})
	}
	attachUsers (arr = []) {
		const formData = new FormData()
		arr.forEach(item => {
			formData.append(`user[${item.id}]`, 1)
		})

		return axios.post(`${baseURL}${this.id}/users`, formData)
			.then(res => {
				return res
			})
			.catch(err => {
				console.error(err)
				return false
			})
	}
	detachUsers (arr = []) {
		const formData = new FormData()
		arr.forEach(item => {
			formData.append(`user[${item.id}]`, 0)
		})

		return axios.post(`${baseURL}${this.id}/users`, formData)
			.then(res => {
				return res
			})
			.catch(err => {
				console.error(err)
				return false
			})
	}
	changeStatusUsers (arr = [], statusId) {
		const formData = new URLSearchParams()
		arr.forEach(item => {
			formData.append(`user[${item.id}][status]`, statusId)
		})

		return axios.put(`${baseURL}${this.id}/users/`, formData)
			.then(res => {
				return res
			})
			.catch(err => {
				console.error(err)
				return false
			})
	}
	setModules (arr) {
		const arrObjects = arr.map(item => new Module({...item, parent: this}))
		return arrObjects.sort(function (a, b) {
			if (a.serial_number > b.serial_number) return 1
			if (a.serial_number <= b.serial_number) return -1
		})
	}

	setMentors (arr) {
		return arr.map(item => new Mentor(item))
	}

	getMembers (params) {
		if (!this.id) return false

		return axios.get(`${baseURL}${this.id}/users`, {
			params
		})
			.then(res => {
				return {
					...res.data,
					data: res.data.data.map(item => new UserWithStatusEvent({
						...item,
						eventId: this.id
					}))
				}
			})
			.catch(err => {
				console.error(err)
				return null
			})
	}
	importMembers (file, type) {
		const formData = new FormData()
		formData.append('file', file)

		if (type === 1) {
			formData.append('event_user_update', type)
		}

		let url = `${baseURL}${this.id}/users/import`
		if (type === 2) {
			url = `/api/user/import`
			formData.append('event_id', this.id)
		}

		return axios.post(url, formData)
			.then(res => {
				return res
			})
			.catch(err => {
				console.error(err)
				return null
			})
	}
	exportMembers (eventUserUpdate = false) {
		const formData = new FormData()
		formData.append('events[id][]', this.id)

		if (this.isOfflineEvent && eventUserUpdate) {
			formData.append('conditions[event_user_update]', '1')
		}

		return axios.post('/api/user/participationReport', formData)
			.then(res => {
				return res
			})
			.catch(err => {
				console.error(err)
				return null
			})
	}

	passingCourseReport () {
		const formData = new FormData()
		formData.append('filters[event_id][]', this.id)

		return axios.post(`/api/user/passingCourseReport`, formData)
			.then(res => {
				return res
			})
			.catch(err => {
				console.error(err)
				return null
			})
	}

	certificates () {
		return axios.post(`${baseURL}${this.id}/certificates`)
			.then(res => res)
			.catch(er => console.error(er))
	}

	createObjectFromJsonString (jsonObj) {
		if (jsonObj) {
			try {
				return JSON.parse(jsonObj)
			} catch (e) {
				return []
			}
		} else {
			return []
		}
	}

	createModuleStructure (jsonObj) {
		const arr = this.createObjectFromJsonString(jsonObj)
		if (!arr || !Array.isArray(arr)) return []

		arr.forEach(item => {
			item.isOpenTab = true
			if (get(item, 'modules')) {
				item.modules = item.modules.filter(moduleId => {
					if (typeof moduleId === 'number') {
						return !!this.modules.find(moduleItem => moduleItem.id === moduleId)
					} else {
						return false
					}
				}).map(moduleId => this.modules.find(moduleItem => moduleItem.id === moduleId))
			} else {
				item.modules = []
			}
		})
		return arr
	}

	moduleStructureForSave () {
		const obj = cloneDeep(this.module_structure).map(({isOpenTab, ...keepAttrs}) => keepAttrs).map(section => {
			return {
				...section,
				modules: section.modules.filter(module => {
					if (typeof module === 'object' && !isNaN(module.id)) {
						return !!module.id
					} else if (typeof module === 'number') {
						return !!module
					} else return false
				}).map(module => {
					if (typeof module === 'object' && module.id) {
						return module.id
					} else if (typeof module === 'number') {
						return module
					}
				})
			}
		})
		return JSON.stringify(obj)
	}

	richContentForSave () {
		const arr = cloneDeep(this.rich_content).map(({isOpenTab, ...keepAttrs}) => keepAttrs).map((el) => {
			if (el && el.content && Array.isArray(el.content.cards)) {
				el.content.cards = el.content.cards.map(({isOpenTab, ...keepAttrs}) => keepAttrs)
			}
			return {
				...el
			}
		})

		return JSON.stringify(arr)
	}

	authorsForSave () {
		const arr = this.authors.map(({isOpenTab, ...keepAttrs}) => keepAttrs)

		return JSON.stringify(arr)
	}

	isValidDate (date) {
		return date && isNaN(date)
	}

	dateToServerFormat (date) {
		return moment(date).format('YYYY-MM-DD HH:mm:ss')
	}

	createErrors (obj) {
		let newObj = {}
		for (let key in obj) {
			const newErrorStr = obj[key].reduce((accumulator, item) => {
				return accumulator + item
			}, '')

			newObj[key] = newErrorStr
		}
		this.errors = newObj
	}

	changeStatusMentors (obj) {
		const formData = new FormData()
		for (const key in obj) {
			console.log(`mentor[${key}]`, obj[key])
			formData.append(`mentor[${key}]`, obj[key])
		}

		return axios.post(`${baseURL}${this.id}/mentor`, formData)
			.then(res => {
				return res
			})
			.catch(err => {
				console.error(err)
				return false
			})
	}
	get isOfflineEvent () {
		return this.eventFormat.id === 1
	}
	get isOnlineEvent () {
		return this.eventFormat.id === 2
	}

	get isWebinar () {
		return this.eventLevel.id === 4
	}

	get isSpecialCourse () {
		return this.eventLevel.id === 5
	}

	get entityName () {
		if (this.isWebinar) return 'Вебинар'
		if (this.isOnlineEvent) return 'Курс'
		if (this.isOfflineEvent) return 'Интенсив'
		return ''
	}

	changeDataUsers (arr = [], field, payload) {
		if (!arr.length) return false

		let urlencoded = new URLSearchParams()
		arr.forEach(item => {
			urlencoded.append(`user[${item.id}][${field}]`, payload)
		})

		return axios.put(`${baseURL}${this.id}/users`, urlencoded)
			.then(res => {
				return res
			})
			.catch(err => {
				console.error(err)
				return false
			})
	}
	sendNotify (notifyParam = '') {
		if (!this.id) return false

		const formData = new FormData()
		if (notifyParam) formData.append('module', notifyParam)

		return axios.post(`${baseURL}${this.id}/notify`, formData)
			.then(res => {
				return res
			})
			.catch(err => console.error(err))
	}

	inviteUser (id) {
		if (!id) return Promise.reject()

		const formData = new FormData()
		formData.append(`user_id`, id)

		return axios.post(`${baseURL}${this.id}/user/invite`, formData)
	}

	getAvailableCount () {
		if (!this.id) return Promise.reject()

		return axios.get(`${baseURL}${this.id}/seats`)
			.then(res => {
				return res.data.data.emptySeats
			})
			.catch(er => console.error(er))
	}

	createImageLogoArray (arr) {
		return arr.map(item => new Image(item))
	}

	static eventFormalList = [
		{
			id: 0,
			name: 'Формат не определен'
		},
		{
			id: 1,
			name: 'Офлайн'
		}, {
			id: 2,
			name: 'Онлайн'
		}
	]
	setEventFormat (init) {
		if (!init || typeof init !== 'number' || typeof init !== 'object') {
			if (typeof init === 'object') {
				return init
			} else {
				const obj = Event.eventFormalList.find(item => item.id === init)
				if (obj) {
					return obj
				} else {
					return {
						id: 0,
						name: 'Формат не определен'
					}
				}
			}
		} else {
			return {
				id: 0,
				name: 'Формат не определен'
			}
		}
	}

	static eventLevelList = [
		{
			id: 0,
			name: 'Уровень не определен'
		},
		{
			id: 1,
			name: 'Федеральный'
		},
		{
			id: 2,
			name: 'Региональный'
		},
		{
			id: 3,
			name: 'Курс'
		},
		{
			id: 4,
			name: 'Вебинар'
		},
		{
			id: 5,
			name: 'Специальный курс'
		}
	]
	static eventTemplateList = [
		{
			id: 0,
			name: 'Шаблон не определен'
		},
		{
			id: 1,
			name: 'АИС2022'
		},
		{
			id: 2,
			name: 'АИС2023'
		},
		{
			id: 3,
			name: 'Эконаставники'
		},
		{
			id: 4,
			name: 'Технонаставники'
		},
		{
			id: 5,
			name: 'Наставник+'
		},
		{
			id: 6,
			name: 'Семейная реликвия'
		},
		{
			id: 7,
			name: 'Технонаставники 2'
		},
		{
			id: 8,
			name: 'Эконаставники 2.0'
		},
		{
			id: 9,
			name: 'Олимпиада по предпринимательству'
		},
		{
			id: 10,
			name: 'Технонаставники 3.0'
		},
		{
			id: 11,
			name: 'Инстурментарий современного наставника'
		},
		{
			id: 12,
			name: 'Технонаставники 4.0'
		},
	]
	static eventLevelListOffline = Event.eventLevelList.filter(el => [0, 1, 2].some(id => id === el.id))
	static eventLevelListOnline = Event.eventLevelList.filter(el => [0, 3, 4, 5].some(id => id === el.id))
	setEventTemplate (init) {
		if (!init || typeof init !== 'number' || typeof init !== 'object') {
			if (typeof init === 'object') {
				return init
			} else {
				const obj = Event.eventTemplateList.find(item => item.id === init)
				if (obj) {
					return obj
				} else {
					return {
						id: 0,
						name: 'Шаблон не определен'
					}
				}
			}
		} else {
			return {
				id: 0,
				name: 'Шаблон не определен'
			}
		}
	}
	setEventLevel (init) {
		if (!init || typeof init !== 'number' || typeof init !== 'object') {
			if (typeof init === 'object') {
				return init
			} else {
				const obj = Event.eventLevelList.find(item => item.id === init)
				if (obj) {
					return obj
				} else {
					return {
						id: 0,
						name: 'Уровень не определен'
					}
				}
			}
		} else {
			return {
				id: 0,
				name: 'Уровень не определен'
			}
		}
	}
	filterRichContent (arr) {
		if (Array.isArray(arr)) {
			return arr.filter(el => el.block === 'repeater' || el.block === 'repeater-text' || el.block === 'accordion')
			.map(el => {
				if (el && el.content && Array.isArray(el.content.cards)) {
					el.content.cards = el.content.cards.map((card) => {
						return {
							...card,
							isOpenTab: true
						}
					})
				}
				return {
					...el,
					isOpenTab: true
				}
			})
		} else return []
	}

	static updateAgreementMultiply (arr, file) {
		if (Array.isArray(arr) && arr.length && file) {
			const formData = new FormData()

			arr.forEach(item => {
				formData.append(`event[${item.id}]`, file)
			})

			return axios.post(`${baseURL}agreement`, formData)
		}
	}

	generateCertificatePreview () {
		return axios.post(`/api/certificate/generate/event/${this.id}/preview`)
			.then((res) => {
				this.preview = get(res, 'data.data') ? new Preview(res.data.data) : null
				return this.preview
			})
			.catch(err => {
				console.error(err)
				return false
			})
	}

	generateUserCertificates (from, to, force = false, hashes = [], params = []) {
		const formData = new FormData()
		if (hashes.length > 0) {
			hashes.forEach(user => formData.append('hashes[]', user.hash))
		} else {
			const dateFrom = moment(from).format('YYYY-MM-DD')
			const dateTo = moment(to).format('YYYY-MM-DD')
			if (!dateFrom || !dateTo) return

			formData.append('date_from', dateFrom)
			formData.append('date_to', dateTo)
		}
		formData.append('regeneration', force ? '1' : '0')

		if (params.length > 0) {
			params.forEach(param => {
				if (param.checked) {
					formData.append('param[]', param.value)
				}
			})
		}

		return axios.post(`/api/certificate/generate/event/${this.id}`, formData)
			.then(res => {
				return res
			})
			.catch(err => {
				console.error(err)
				return false
			})
	}

	/**
	 * @param {Company} company
	 * @return Promise<undefined>
	 */
	async addPartnerCompany (company) {
		try {
			const partners = [...this.partner.map(item => item.id), company.id]
			const { data } = await axios.post(baseURL + this.id, { partners })
			this.partner = get(data.data, 'partner') && Array.isArray(data.data.partner) ? data.data.partner.map(args => new Company(args)) : []
		} catch (e) {
			if (process.env.NODE_ENV !== 'production') {
				console.log(e)
			}
		}
	}

	/**
	 * @param {Number} id
	 * @return Promise<undefined>
	 */
	async removePartnerCompany (id) {
		try {
			const partners = this.partner.filter(item => item.id !== id).map(item => item.id)
			const { data } = await axios.post(baseURL + this.id, { partners })
			this.partner = get(data.data, 'partner') && Array.isArray(data.data.partner) ? data.data.partner.map(args => new Company(args)) : []
		} catch (e) {
			if (process.env.NODE_ENV !== 'production') {
				console.log(e)
			}
		}
	}

	/**
	 * @param {Company} company
	 * @return Promise<undefined>
	 */
	async addOrganizationCompany (company) {
		try {
			const organization = [...this.organization.map(item => item.id), company.id]
			const { data } = await axios.post(baseURL + this.id, { organization })
			this.organization = get(data.data, 'organization') && Array.isArray(data.data.organization) ? data.data.organization.map(args => new Company(args)) : []
		} catch (e) {
			if (process.env.NODE_ENV !== 'production') {
				console.log(e)
			}
		}
	}

	/**
	 * @param {Number} id
	 * @return Promise<undefined>
	 */
	async removeOrganizationCompany (id) {
		try {
			const organization = this.organization.filter(item => item.id !== id).map(item => item.id)
			const { data } = await axios.post(baseURL + this.id, { organization })
			this.organization = get(data.data, 'organization') && Array.isArray(data.data.organization) ? data.data.organization.map(args => new Company(args)) : []
		} catch (e) {
			if (process.env.NODE_ENV !== 'production') {
				console.log(e)
			}
		}
	}

	async copy () {
		return await axios.post(`${baseURL}${this.id}/copy`)
			.then(res => {
				return res
			})
			.catch(err => {
				console.error(err)
				return false
			})
	}

	setArrayClassObject (arr, ClassEntity = null) {
		if (Array.isArray(arr) && arr.length) {
			if (ClassEntity) {
				return arr.map(item => new ClassEntity(item))
			} else {
				return arr
			}
		} else {
			return []
		}
	}

	get gameLink () {
		return this.front_url ? `${this.front_url}/game` : ''
	}

	certificateParamList () {
		let partParams = []
		if (this.isOfflineEvent) {
			partParams = [{
				label: 'Дата начала интенсива',
				value: 'start_at',
				checked: true
			}, {
				label: 'Дата конца интенсива',
				value: 'finish_at',
				checked: true
			}, {
				label: 'Место проведения',
				value: 'placeName',
				checked: true
			}, {
				label: 'Город',
				value: 'city',
				checked: true
			}]
		}

		if (this.isOnlineEvent) {
			partParams = [{
				label: 'Дата начала курса',
				value: 'start_at',
				checked: true
			}, {
				label: 'Дата конца курса',
				value: 'finish_at',
				checked: true
			}, {
				label: 'Количество модулей',
				value: 'num_modules',
				checked: true
			}, {
				label: 'Количество часов',
				value: 'number_hours',
				checked: true
			}]
		}

		return [
			...CERTIFICATE_PARAM_BASIC,
			...partParams
		]
	}

	/**
	 * @description Метод используется для связи фронта с админкой при редактировании мероприятий.
	 * С помощью него реализовано обновление данных при редактировании в реальном времени, без отправки их на сервер.
	 * Фронт необходимо выводить в iframe, с передачей сообщения: "postMessage({editMode: true}, '*')"
	 * @return {Object} Возвращает объект с данными, приведенными в формат, необходимый для вывода на фронте
	 * */
	getEventDataForUpdate () {
		const setImage = (newImage, defaultImage) => {
			if (newImage) {
				if (newImage === REMOVE_IMAGE) {
					return new ImageCrud()
				} else {
					return new ImageCrud({default: newImage})
				}
			} else if (defaultImage) {
				return defaultImage
			} else return new ImageCrud()
		}

		return {
			published: this.published,
			hidden: this.hidden,
			event_format: this.eventFormat,
			event_level: this.eventLevel,
			publish_title: this.publishTitle,
			full_name: this.full_name,
			publish_info: this.publishInfo,
			full_info: this.full_info,
			info: this.info,
			contact_email: this.contact_email,
			contact_phone: this.contact_phone,
			start_at: this.start_at,
			finish_at: this.finish_at,
			reg_finish_at: this.reg_finish_at,
			custom_status_for_users: this.custom_status_for_users,
			participants_number: this.participants_number,
			rich_content: this.richContentForSave(),
			module_structure: this.moduleStructureForSave(),
			modules: this.module_structure.flatMap(el => el.modules),
			logo: setImage(this.new_logo_url, this.logo),
			logo_micro: setImage(this.new_logo_micro_url, this.logo_micro),
			agreement: this.new_agreement_url ? new Agreement({url: this.new_agreement_url}) : this.agreement,
			city: this.city,
			place: this.place,
			timetable: this.timetable,
			form: {id: this.form.id, content: JSON.stringify(this.form.content)},
			authors: this.authors,
			reviews: this.reviews,
			promo: this.promo,
			preview: this.preview,
			applications_all: this.applications_all,
			applications_approved: this.applications_approved,
			applications_pending: this.applications_pending,
			applications_rejected: this.applications_rejected,
			graduated_applications: this.graduated_applications,
			video_url: this.video_url,
			feedback_form_url: this.feedback_form_url
		}
	}
}

export class EventWithUserStatus extends Event {
	constructor (init = {}) {
		super(init)
		this.status = get(init, 'status.status') ? new UserStatus(init.status) : new UserStatus()
		this.formResult = get(init, 'form_result', false)
		this.formAnswers = get(init, 'form_answers') && Array.isArray(init.form_answers) ? init.form_answers : []
		this.certificateAt = get(init, 'certificate_at', false)
		this.certificateNumber = get(init, 'certificate_number', '')
		this.certificateSeries = get(init, 'certificate_series', '')
		this.certificateUrl = get(init, 'certificate_url', null)
		this.hash = get(init, 'hash') ? init.hash : ''
		this.progress = get(init, 'progress', '')
		this.allModulesAccess = get(init, 'all_modules_access', null)

		/* this.formAnswers.forEach(item => {
			if (item.type === 'separator') return
			this[`answers_${item.name}`] = item
		}) */
	}
	get hasCertificate () {
		return this.status.status === 4
	}
}



// WEBPACK FOOTER //
// ./src/assets/js/Event.class.js